import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus, ShoppingBag, Star, Sparkles, Zap, Droplets, Shield, ChevronRight, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCart } from '@/context/CartContext';
import { toast } from 'sonner';
import AfterpayPriceTag from '@/components/AfterpayPriceTag';
import { LoyaltyPointsBadge } from '@/components/LoyaltyPointsBadge';

const bundleOptions = [
  {
    category: 'Botox',
    icon: Zap,
    color: 'violet',
    items: [
      { id: 'bb-11s', name: '11s (Frown Lines)', price: 249, area: '1 area' },
      { id: 'bb-forehead', name: 'Forehead', price: 199, area: '1 area' },
      { id: 'bb-crows', name: "Crow's Feet", price: 299, area: '1 area' },
      { id: 'bb-big3', name: 'The Big 3 (Best Value)', price: 499, area: '3 areas' },
      { id: 'bb-lipflip', name: 'Lip Flip', price: 149, area: '1 area' },
      { id: 'bb-full', name: 'Full Upper Face', price: 599, area: '7 areas' },
    ],
  },
  {
    category: 'Skin Boosters',
    icon: Droplets,
    color: 'teal',
    items: [
      { id: 'bb-glow-face', name: 'Baby Glow Face (3ML)', price: 699, area: 'Full face' },
      { id: 'bb-glow-neck', name: 'Baby Glow Face + Neck', price: 899, area: 'Face + Neck' },
      { id: 'bb-glow-eyes', name: 'Eye Revival', price: 449, area: 'Under eyes' },
      { id: 'bb-glow-hands', name: 'Hand Rejuvenation', price: 399, area: 'Both hands' },
    ],
  },
  {
    category: 'Dermal Fillers',
    icon: Star,
    color: 'purple',
    items: [
      { id: 'bb-filler-1', name: '1 Syringe (Any Area)', price: 499, area: 'Lips, cheeks, etc.' },
      { id: 'bb-filler-2', name: '2 Syringes + FREE Tox', price: 950, area: 'Best starter' },
      { id: 'bb-filler-3', name: '3 Syringes + FREE Tox', price: 1299, area: 'Full refresh' },
    ],
  },
  {
    category: 'Microneedling',
    icon: Shield,
    color: 'rose',
    items: [
      { id: 'bb-mn-1', name: 'Medical Microneedling', price: 350, area: '1 session' },
      { id: 'bb-mn-prp', name: 'Microneedling + PRP', price: 549, area: 'Vampire facial' },
      { id: 'bb-mn-vamp', name: 'Microneedling + VAMP', price: 549, area: 'Salmon DNA' },
      { id: 'bb-mn-booster', name: 'Microneedling + 3ML Booster', price: 899, area: 'Combo' },
    ],
  },
  {
    category: 'Sculptra',
    icon: Sparkles,
    color: 'amber',
    items: [
      { id: 'bb-sc-1', name: '1 Vial Sculptra', price: 799, area: 'Targeted' },
      { id: 'bb-sc-2', name: '2 Vials Sculptra', price: 1500, area: 'Most common' },
    ],
  },
];

const colorMap = {
  violet: { bg: 'bg-violet-500/10', border: 'border-violet-500/30', text: 'text-violet-400', active: 'bg-violet-600', ring: 'ring-violet-400' },
  teal: { bg: 'bg-teal-500/10', border: 'border-teal-500/30', text: 'text-teal-400', active: 'bg-teal-600', ring: 'ring-teal-400' },
  purple: { bg: 'bg-purple-500/10', border: 'border-purple-500/30', text: 'text-purple-400', active: 'bg-purple-600', ring: 'ring-purple-400' },
  rose: { bg: 'bg-rose-500/10', border: 'border-rose-500/30', text: 'text-rose-400', active: 'bg-rose-600', ring: 'ring-rose-400' },
  amber: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400', active: 'bg-amber-600', ring: 'ring-amber-400' },
};

const BundleBuilder = () => {
  const [selected, setSelected] = useState([]);
  const [expandedCat, setExpandedCat] = useState('Botox');
  const { addToCart, isInCart } = useCart();

  const toggleItem = (item) => {
    setSelected(prev => {
      const exists = prev.find(s => s.id === item.id);
      if (exists) return prev.filter(s => s.id !== item.id);
      return [...prev, item];
    });
  };

  const isSelected = (id) => selected.some(s => s.id === id);

  const totalPrice = selected.reduce((sum, s) => sum + s.price, 0);
  const individualTotal = totalPrice;
  const bundleDiscount = selected.length >= 3 ? Math.round(totalPrice * 0.05) : selected.length >= 2 ? Math.round(totalPrice * 0.02) : 0;
  const bundlePrice = totalPrice - bundleDiscount;

  const addBundleToCart = () => {
    const bundleName = selected.map(s => s.name).join(' + ');
    const cartItem = {
      id: `bundle-${selected.map(s => s.id).sort().join('-')}`,
      name: `Custom Bundle: ${selected.length} treatments`,
      category: bundleName.length > 60 ? bundleName.slice(0, 60) + '...' : bundleName,
      price: `$${bundlePrice.toLocaleString()}`,
      type: 'combo',
      duration: selected.length * 30,
    };
    addToCart(cartItem);
    toast.success(`Custom bundle added to cart — ${selected.length} treatments!`, { duration: 3000 });
    setSelected([]);
  };

  return (
    <section className="py-16 sm:py-20 bg-gradient-to-b from-white to-gray-50" data-testid="bundle-builder">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-rose-50 rounded-full mb-4">
            <Sparkles className="w-4 h-4 text-rose-600" />
            <span className="text-rose-700 text-xs font-bold uppercase tracking-[0.15em]">Build Your Perfect Treatment</span>
          </div>
          <h2 className="text-3xl sm:text-4xl mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
            Treatment <span className="italic text-rose-600">Bundle Builder</span>
          </h2>
          <p className="text-gray-500 max-w-xl mx-auto text-sm sm:text-base">
            Mix and match treatments to create your perfect appointment. Select 2+ for extra savings.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left: Treatment Selector */}
          <div className="lg:col-span-2 space-y-3">
            {bundleOptions.map((cat) => {
              const c = colorMap[cat.color];
              const isOpen = expandedCat === cat.category;
              const CatIcon = cat.icon;
              const catSelectedCount = cat.items.filter(item => isSelected(item.id)).length;
              return (
                <div key={cat.category} className={`rounded-xl border overflow-hidden transition-all ${isOpen ? `${c.border} shadow-sm` : 'border-gray-200'}`}>
                  <button
                    onClick={() => setExpandedCat(isOpen ? null : cat.category)}
                    className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${c.bg}`}>
                        <CatIcon className={`w-5 h-5 ${c.text}`} />
                      </div>
                      <div className="text-left">
                        <h3 className="font-semibold text-gray-900">{cat.category}</h3>
                        <p className="text-xs text-gray-500">{cat.items.length} options</p>
                      </div>
                      {catSelectedCount > 0 && (
                        <span className={`${c.active} text-white text-xs font-bold px-2 py-0.5 rounded-full ml-2`}>{catSelectedCount} selected</span>
                      )}
                    </div>
                    <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                  </button>

                  <AnimatePresence>
                    {isOpen && (
                      <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
                        <div className="px-4 pb-4 space-y-2">
                          {cat.items.map((item) => {
                            const sel = isSelected(item.id);
                            return (
                              <button
                                key={item.id}
                                onClick={() => toggleItem(item)}
                                className={`w-full flex items-center justify-between p-3.5 rounded-xl border-2 transition-all ${
                                  sel ? `${c.border} ${c.bg}` : 'border-gray-100 hover:border-gray-200 bg-white'
                                }`}
                                data-testid={`bundle-item-${item.id}`}
                              >
                                <div className="flex items-center gap-3">
                                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                                    sel ? `${c.active} border-transparent` : `border-gray-300`
                                  }`}>
                                    {sel && <Check className="w-3.5 h-3.5 text-white" />}
                                  </div>
                                  <div className="text-left">
                                    <p className={`font-medium text-sm ${sel ? 'text-gray-900' : 'text-gray-700'}`}>{item.name}</p>
                                    <p className="text-xs text-gray-400">{item.area}</p>
                                  </div>
                                </div>
                                <span className={`font-bold ${sel ? c.text : 'text-gray-900'}`}>${item.price}</span>
                              </button>
                            );
                          })}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>

          {/* Right: Bundle Summary */}
          <div className="lg:col-span-1">
            <div className="sticky top-32 bg-white rounded-2xl border-2 border-gray-200 shadow-lg overflow-hidden">
              <div className="bg-stone-900 px-6 py-4">
                <h3 className="text-white font-semibold flex items-center gap-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                  <ShoppingBag className="w-5 h-5 text-amber-400" />
                  Your Bundle
                </h3>
                <p className="text-stone-400 text-xs mt-0.5">{selected.length} treatment{selected.length !== 1 ? 's' : ''} selected</p>
              </div>

              <div className="p-5">
                {selected.length === 0 ? (
                  <div className="text-center py-8">
                    <ShoppingBag className="w-12 h-12 text-gray-200 mx-auto mb-3" />
                    <p className="text-gray-400 text-sm">Select treatments to build your bundle</p>
                    <p className="text-gray-300 text-xs mt-1">2+ treatments = extra savings</p>
                  </div>
                ) : (
                  <>
                    <div className="space-y-2.5 mb-5 max-h-60 overflow-y-auto">
                      {selected.map(s => (
                        <div key={s.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2.5">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{s.name}</p>
                          </div>
                          <div className="flex items-center gap-2 ml-2">
                            <span className="text-sm font-semibold text-gray-700">${s.price}</span>
                            <button onClick={() => toggleItem(s)} className="p-1 hover:bg-red-50 rounded text-gray-400 hover:text-red-500">
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Pricing */}
                    <div className="border-t border-gray-200 pt-4 space-y-2">
                      {bundleDiscount > 0 && (
                        <>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-500">Individual total</span>
                            <span className="text-gray-400 line-through">${individualTotal.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-green-600 font-medium">Bundle discount ({selected.length >= 3 ? '5%' : '2%'})</span>
                            <span className="text-green-600 font-semibold">-${bundleDiscount.toLocaleString()}</span>
                          </div>
                        </>
                      )}
                      <div className="flex justify-between items-end pt-1">
                        <span className="text-gray-900 font-semibold">Your price</span>
                        <div className="text-right">
                          <span className="text-2xl font-bold text-gray-900">${bundlePrice.toLocaleString()}</span>
                          <span className="text-gray-400 text-sm ml-1">+tax</span>
                        </div>
                      </div>
                      <AfterpayPriceTag price={bundlePrice} className="justify-end" />
                      <LoyaltyPointsBadge price={bundlePrice} className="justify-end" />
                    </div>

                    {/* Add to Cart */}
                    <Button
                      onClick={addBundleToCart}
                      className="w-full mt-5 py-6 bg-rose-600 hover:bg-rose-700 text-white font-semibold text-base rounded-xl"
                      data-testid="bundle-add-to-cart"
                    >
                      <ShoppingBag className="w-5 h-5 mr-2" />
                      Add Bundle to Cart
                    </Button>

                    {selected.length < 2 && (
                      <p className="text-center text-xs text-amber-600 mt-2 font-medium">Add 1 more for a 2% bundle discount!</p>
                    )}
                    {selected.length === 2 && (
                      <p className="text-center text-xs text-green-600 mt-2 font-medium">Add 1 more for a 5% discount!</p>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BundleBuilder;
